import styled from '@emotion/styled';
import { rgba } from 'emotion-rgba';

import { type FilePreviewSize } from './FilePreview';

type PreviewContentProps = FilePreviewSize & { $variant?: 'default' | 'a4' | 'viewer' };

// Portrait A4 ratio (210mm / 297mm ≈ 0.7071). The frame is anchored to the
// viewport height and derives its width from this ratio, so it always fits on
// screen. Width is computed explicitly (rather than via `aspect-ratio`) because
// a block element's `width: auto` stretches to its container and would override
// the ratio.
const A4_WIDTH_TO_HEIGHT = 0.7071;
// Space reserved above/below the frame (page header, paddings, floating panel).
const A4_VERTICAL_RESERVE = '220px';
const A4_FRAME_HEIGHT = `calc(100vh - ${A4_VERTICAL_RESERVE})`;
const A4_FRAME_WIDTH = `calc((100vh - ${A4_VERTICAL_RESERVE}) * ${A4_WIDTH_TO_HEIGHT})`;

export const Preview = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.strokeDarkGrey};
  box-shadow: 0 3px 8px 0 ${({ theme }) => rgba(theme.colors.dark, 0.1)};
  border-radius: 10px;
  display: inline-block;
  padding: 20px 30px 30px 30px;
`;

export const PreviewTitle = styled.div`
  font-weight: 600;
  text-transform: uppercase;
`;

export const PreviewContent = styled.div<PreviewContentProps>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '622px'};
  min-height: 500px;
  background-color: ${({ theme }) => theme.colors.textDisabled};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;

  ${({ $variant }) =>
    $variant === 'a4' &&
    `
    height: ${A4_FRAME_HEIGHT};
    max-height: ${A4_FRAME_HEIGHT};
    width: ${A4_FRAME_WIDTH};
    max-width: 100%;
    min-height: 0;
  `}

  img {
    width: 100%;
    max-height: 100%;
  }

  .file-preview-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      cursor: zoom-in;
    }
  }
`;

export const Empty = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  width: 72px;
  height: 72px;
  background-color: ${({ theme }) => theme.colors.textDisabled};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
`;

export const EmptyTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-top: 20px;
`;

export const EmptyDescription = styled.div`
  color: ${({ theme }) => theme.colors.text400};
  margin-bottom: 24px;
`;
